import { useState } from 'react';
import { useRecoilState } from 'recoil';
import { langState } from '../state/langState';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import logo from '../assets/logo.png';

const Navbar = () => {
  const { i18n, t } = useTranslation();

  const [lang, setLang] = useRecoilState(langState);

  const [open, setOpen] = useState(false);

  function changeLang(lng) {
    setLang(lng);
    i18n.changeLanguage(lng);
  }

  /* ===============================
     NAV ITEM
  =============================== */

  const NavItem = ({ to, children }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `
        relative
        transition

        hover:text-[#1F1F1F]

        ${
          isActive
            ? 'text-[#1F1F1F] after:absolute after:-bottom-1 after:left-0 after:w-full after:h-[2px] after:bg-[#C6A75E]'
            : ''
        }
        `
      }
    >
      {children}
    </NavLink>
  );

  const DropdownItem = ({ to, children }) => (
    <NavLink
      to={to}
      className='
        block
        px-5
        py-2.5

        text-sm
        text-[#6F6A63]

        transition

        hover:bg-[#F7F6F2]
        hover:text-[#1F1F1F]
      '
    >
      {children}
    </NavLink>
  );

  const LangButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      className={`
        px-3
        py-1

        rounded-full

        text-xs

        transition

        ${
          active
            ? 'bg-white shadow-sm text-[#1F1F1F]'
            : 'text-[#8A867F] hover:text-[#1F1F1F]'
        }
      `}
    >
      {children}
    </button>
  );

  const MobileLink = ({ to, onClick, children }) => (
    <NavLink
      to={to}
      onClick={onClick}
      className='
        text-[15px]
        font-medium

        transition

        hover:text-[#1F1F1F]
      '
    >
      {children}
    </NavLink>
  );

  /* ===============================
     UI
  =============================== */

  return (
    <>
      <header
        className='
          fixed
          top-0
          z-50
          w-full

          bg-white/80
          backdrop-blur-xl

          border-b
          border-[#E7E2D9]
        '
      >
        <div className='flex items-center justify-between px-5 mx-auto h-14 sm:h-16 sm:px-6 lg:px-8 max-w-7xl'>
          {/* LOGO */}

          <NavLink
            to='/'
            className='
              flex
              items-center
              gap-3

              font-semibold
              tracking-wide

              text-[#1F1F1F]

              transition
              hover:opacity-80
            '
          >
            <img src={logo} alt='logo' className=' w-7 h-7 sm:w-8 sm:h-8' />

            <span
              className='
                text-[14px]
                sm:text-[15px]
                lg:text-[16px]
              '
            >
              Tomado Design
            </span>
          </NavLink>

          {/* DESKTOP NAV */}

          <nav
            className='
              hidden
              md:flex

              items-center

              gap-8
              lg:gap-10

              text-sm
              font-medium

              text-[#6F6A63]
            '
          >
            {/* STUDIO */}

            <NavItem to='/studio'>Studio</NavItem>

            {/* PORTFOLIO */}

            <div className='relative group'>
              <button
                className='
                  flex
                  items-center
                  gap-1

                  transition
                  hover:text-[#1F1F1F]
                '
              >
                {t('nav.portfolio')}

                <ChevronDown
                  size={16}
                  className='transition group-hover:rotate-180'
                />
              </button>

              <div className='absolute left-0 invisible transition-all duration-200 translate-y-2 opacity-0 top-full group-hover:visible group-hover:opacity-100 group-hover:translate-y-0'>
                <div
                  className='
                    w-56
                    py-3

                    bg-white
                    border
                    border-[#E7E2D9]

                    rounded-2xl
                    shadow-xl
                  '
                >
                  <DropdownItem to='/portfolio/branding'>Branding</DropdownItem>

                  <DropdownItem to='/portfolio/web'>Web Design</DropdownItem>

                  <DropdownItem to='/portfolio/development'>
                    Development
                  </DropdownItem>

                  <DropdownItem to='/portfolio/graphic'>
                    Graphic Design
                  </DropdownItem>
                </div>
              </div>
            </div>

            {/* FAQ */}

            <NavItem to='/FAQ'>FAQ</NavItem>

            {/* LANGUAGE */}

            <div
              className='
                flex
                items-center
                gap-1

                px-1
                py-1

                ml-4

                bg-[#F7F6F2]
                border
                border-[#E7E2D9]

                rounded-full
              '
            >
              <LangButton
                active={lang === 'en'}
                onClick={() => changeLang('en')}
              >
                EN
              </LangButton>

              <LangButton
                active={lang === 'kr'}
                onClick={() => changeLang('kr')}
              >
                KR
              </LangButton>
            </div>
          </nav>

          {/* MOBILE BUTTON */}

          <button
            onClick={() => setOpen(true)}
            className='
              md:hidden
              text-[#1F1F1F]
            '
          >
            <Menu size={22} />
          </button>
        </div>
      </header>

      {/* ===============================
         MOBILE DRAWER
      =============================== */}

      {/* overlay */}

      <div
        onClick={() => setOpen(false)}
        className={`
          fixed
          inset-0
          z-40

          bg-black/30
          backdrop-blur-sm

          transition
          ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      />

      {/* drawer */}

      <div
        className={`
          fixed
          top-0
          right-0
          z-50

          h-screen
          w-[90%]

          bg-white

          shadow-2xl

          transition-transform
          duration-300

          ${open ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        <div className='flex flex-col h-full px-6 py-6 '>
          {/* CLOSE */}

          <div className='flex justify-end'>
            <button onClick={() => setOpen(false)}>
              <X size={24} />
            </button>
          </div>

          {/* STUDIO */}

          <SectionTitle>Studio</SectionTitle>

          <MobileLink to='/studio' onClick={() => setOpen(false)}>
            Mockup Studio
          </MobileLink>

          <Divider />

          {/* PORTFOLIO */}

          <SectionTitle>{t('nav.portfolio')}</SectionTitle>

          <MobileLink to='/portfolio/branding' onClick={() => setOpen(false)}>
            Branding
          </MobileLink>

          <MobileLink to='/portfolio/web' onClick={() => setOpen(false)}>
            Web Design
          </MobileLink>

          <MobileLink
            to='/portfolio/development'
            onClick={() => setOpen(false)}
          >
            Development
          </MobileLink>

          <MobileLink to='/portfolio/graphic' onClick={() => setOpen(false)}>
            Graphic Design
          </MobileLink>

          <Divider />

          {/* LANGUAGE */}

          <SectionTitle>Language</SectionTitle>

          <div className='flex gap-3 mt-3'>
            <LangButton active={lang === 'en'} onClick={() => changeLang('en')}>
              EN
            </LangButton>

            <LangButton active={lang === 'kr'} onClick={() => changeLang('kr')}>
              KR
            </LangButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;

/* ===============================
   UI COMPONENTS
=============================== */

const SectionTitle = ({ children }) => (
  <p className='mt-8 mb-3 text-xs font-semibold tracking-widest uppercase text-neutral-400'>
    {children}
  </p>
);

const Divider = () => (
  <div
    className='
      h-px
      bg-[#E7E2D9]

      my-6
    '
  />
);
